import { API_BASE_URL } from '../config/apiConfig';

/** Default static logo (served from backend `/uploads` without login). */
export const DEFAULT_COMPANY_LOGO_PATH = '/uploads/logos/company-logo.svg';

/**
 * Build absolute URL for company logo path from Application Setting value.
 * @param {string} value — e.g. "/uploads/logos/company-logo.svg"
 * @returns {string|null}
 */
export function resolveCompanyLogoUrl(value) {
  if (!value || typeof value !== 'string') return null;

  let logoUrl = value.trim();
  if (!logoUrl) return null;

  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
    return logoUrl;
  }

  const baseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
  if (logoUrl.startsWith('/')) {
    return `${baseUrl}${logoUrl}`;
  }
  return `${baseUrl}/${logoUrl}`;
}

/**
 * Logo URL for login / public pages — no auth required (static uploads path).
 * @returns {string}
 */
export function getPublicCompanyLogoUrl() {
  return resolveCompanyLogoUrl(DEFAULT_COMPANY_LOGO_PATH);
}

/**
 * Prefer public branding API (if configured), else static default — works before login.
 * @returns {Promise<string>}
 */
export async function resolveCompanyLogoUrlForAuthPage() {
  const fallback = getPublicCompanyLogoUrl();

  try {
    const response = await fetch(`${API_BASE_URL}/public/branding/company-logo`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.ok) {
      const data = await response.json();
      if (data?.path) {
        return resolveCompanyLogoUrl(data.path) ?? fallback;
      }
    }
  } catch {
    /* use static fallback */
  }

  return fallback;
}

/**
 * Authenticated pages: Application Setting, with static fallback.
 * @returns {Promise<string|null>}
 */
export async function fetchCompanyLogoUrl() {
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(
      `${API_BASE_URL}/application-setting/key/companyLogo`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );

    if (response.ok) {
      const setting = await response.json();
      if (setting?.value && setting.isActive !== false) {
        return resolveCompanyLogoUrl(setting.value);
      }
    }
  } catch {
    /* ignore */
  }

  return getPublicCompanyLogoUrl();
}
